import { createElement } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import {
    Box,
    List,
    ListItem,
    ListItemText,
    Alert,
    Typography,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import { useTranslate, Link } from 'react-admin';
import { numberFormatter, appIcons } from './common';
import { Handler, MpassByKuntaItem } from './types';

const pct = (part: number, total: number): string =>
    total > 0 ? `${Math.round((part / total) * 100)} %` : '0 %';

const MpassByKuntaList = (data: MpassByKuntaItem[]) => {
    const translate = useTranslate();

    if (!data || data.length === 0) {
        return (
            <Alert severity="warning" sx={{ padding: 10 }}>
                {translate('statistics.nodata')}
            </Alert>
        );
    }

    return (
        <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {data.map(item => {
                const total =
                    item.teacher_count + item.student_count + item.pupil_count;
                const mpassTotal =
                    item.teacher_mpass_count +
                    item.student_mpass_count +
                    item.pupil_mpass_count;
                return (
                    <ListItem key={item.maakunta}>
                        <ListItemText
                            primary={`${item.maakunta} — ${pct(mpassTotal, total)} MPASSid (${item.school_count} ${translate('statistics.mpassbykunta.schools')})`}
                            secondary={`${translate('statistics.mpassbykunta.teachers')} ${pct(item.teacher_mpass_count, item.teacher_count)} | ${translate('statistics.mpassbykunta.students')} ${pct(item.student_mpass_count, item.student_count)} | ${translate('statistics.mpassbykunta.pupils')} ${pct(item.pupil_mpass_count, item.pupil_count)} | ${translate('statistics.fields.all')} ${numberFormatter.format(total)}`}
                        />
                    </ListItem>
                );
            })}
        </List>
    );
};

const MpassByKuntaGraph = (data: MpassByKuntaItem[]) => {
    const translate = useTranslate();

    if (!data || data.length === 0) {
        return (
            <Alert severity="warning" sx={{ padding: 10 }}>
                {translate('statistics.nodata')}
            </Alert>
        );
    }

    // Show top 20 seutukuntas by total users for readability
    const sorted = [...data]
        .sort(
            (a, b) =>
                b.teacher_count +
                b.student_count +
                b.pupil_count -
                (a.teacher_count + a.student_count + a.pupil_count)
        )
        .slice(0, 20);

    const xAxis = sorted.map(item => item.maakunta);

    const seriesTeacher = {
        data: sorted.map(item =>
            item.teacher_count > 0
                ? Math.round(
                      (item.teacher_mpass_count / item.teacher_count) * 100
                  )
                : 0
        ),
        label: translate('statistics.mpassbykunta.teachers'),
    };

    const seriesStudent = {
        data: sorted.map(item =>
            item.student_count > 0
                ? Math.round(
                      (item.student_mpass_count / item.student_count) * 100
                  )
                : 0
        ),
        label: translate('statistics.mpassbykunta.students'),
    };

    const seriesPupil = {
        data: sorted.map(item =>
            item.pupil_count > 0
                ? Math.round((item.pupil_mpass_count / item.pupil_count) * 100)
                : 0
        ),
        label: translate('statistics.mpassbykunta.pupils'),
    };

    return (
        <BarChart
            height={400}
            series={[seriesTeacher, seriesStudent, seriesPupil]}
            xAxis={[
                {
                    data: xAxis,
                    scaleType: 'band',
                },
            ]}
            yAxis={[{ label: 'MPASSid %', max: 100 }]}
        />
    );
};

const MpassByKunta = (props: { data: MpassByKuntaItem[] | null } & Handler) => {
    const translate = useTranslate();
    const { data, handler, mode } = props;
    const title = translate('statistics.mpassbykunta.title');
    const tooltip = translate('statistics.mpassbykunta.tooltip');
    const totalMaakunnat = data?.length || 0;

    return (
        <>
            {createElement(handler, {
                title,
                subtitle: `${totalMaakunnat} maakuntaa`,
                tooltip,
                icon: appIcons.school,
                children: (
                    <>
                        {(() => {
                            switch (mode) {
                                case 'List':
                                    return MpassByKuntaList(data);
                                case 'Graph':
                                    return MpassByKuntaGraph(data);
                                default:
                                    return null;
                            }
                        })()}
                        <Box sx={{ p: 1, textAlign: 'right' }}>
                            <Link to="/schoolmap">
                                <Typography
                                    variant="body2"
                                    color="primary"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <MapIcon fontSize="small" />
                                    {translate(
                                        'statistics.mpassbykunta.show_on_map'
                                    )}
                                </Typography>
                            </Link>
                        </Box>
                    </>
                ),
            })}
        </>
    );
};

export default MpassByKunta;
